import type { SegmentRule } from "@domains/segments/shared/segmentsTypes.ts";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { useSegmentsClient } from "@domains/segments/client/api/segmentsQuery.ts";
import { AppliedRulePills } from "@domains/segments/client/components/rules/AppliedRulePills.tsx";
import { createQueryOptions } from "@packages/core-client/api/createQueryOptions.ts";
import { useSession } from "@packages/core-client/session/SessionContext.tsx";
import { useSkeletonState } from "@packages/core-client/hooks/useSkeletonState.ts";
import { useI18nextInstance } from "@squide/i18next";
import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { setTeamAdHocFilter, useTeamAdHocDraft, useTeamAdHocFilter } from "../hooks/teamAdHocFilter.ts";
import { i18NextInstanceKey } from "../i18n/i18next.ts";

const PLACEHOLDER_FIELDS = { items: [] };

const PILL_PLACEHOLDER = [{ id: "pill-skeleton-1" }, { id: "pill-skeleton-2" }];

export function TeamFilterPills() {
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("TeamFilterActions", { i18n: i18nextInstance });
    const adHocFilter = useTeamAdHocFilter();
    const draft = useTeamAdHocDraft();

    const session = useSession();
    const { queryUtils } = useSegmentsClient();
    const orgId = session?.organizationId ?? "";

    const {
        data: employeeFields,
        isPending: areFieldsPending,
        isError: haveFieldsFailed
    } = useQuery(
        createQueryOptions(queryUtils.listEmployeeFields.queryOptions({ input: { orgId } }), {
            throwOnError: false
        })
    );
    const { isLoading: areFieldsLoading, hasContent: hasFieldsContent } = useSkeletonState({ isPending: areFieldsPending, data: employeeFields ? [employeeFields] : undefined, placeholder: [PLACEHOLDER_FIELDS] });

    const handleRemoveRule = useCallback(
        (rule: SegmentRule) => {
            if (!adHocFilter || !draft) {
                return;
            }

            const nextDraftRules = draft.rules.filter(candidate => candidate !== rule);

            if (nextDraftRules.length === 0) {
                setTeamAdHocFilter(null);

                return;
            }

            const lockRules = adHocFilter.rules.slice(0, adHocFilter.rules.length - draft.rules.length);
            setTeamAdHocFilter({ filter: { matchType: adHocFilter.matchType, rules: [...lockRules, ...nextDraftRules] }, draft: { matchType: draft.matchType, rules: nextDraftRules } });
        },
        [adHocFilter, draft]
    );

    if (!adHocFilter || !draft) {
        return null;
    }

    if (!hasFieldsContent) {
        return null;
    }

    if (areFieldsLoading || !employeeFields) {
        return (
            <div className="flex gap-1">
                {PILL_PLACEHOLDER.map(placeholder => (
                    <Skeleton key={placeholder.id} isLoading inert aria-hidden="true">
                        <span className="inline-block h-11 w-24 rounded-full" />
                    </Skeleton>
                ))}
            </div>
        );
    }

    if (haveFieldsFailed) {
        return null;
    }

    return <AppliedRulePills rules={draft.rules} fields={employeeFields.items} aria-label={t("appliedRulesLabel")} onRemove={handleRemoveRule} />;
}
